// Path-addressed boolean-tree GROUP-level edit planners (Slice 3B.4). Each op
// resolves a NodePath into the `clause`-selected tree — `structure.whereTree`
// by default, `structure.havingTree` for `clause: 'having'` (Plan 3) — derives
// a byte range, and emits PlannedByteEdit(s). Engine-agnostic: parens / AND /
// OR / NOT are SQL punctuation, identical across all five engines.
//
// Ops: set-connector (flip a group AND↔OR), toggle-negate (add/remove NOT on a
// leaf or group), unwrap-group (drop an explicit group's parens), add-where-group
// (append a new parenthesised child group with a starter predicate). A
// parenthesis or connector written after a node that ends in a line comment
// goes on the next line (`outsideLineComments`).

import type { PlannedByteEdit, StructuralEdit } from '../types';
import { scanTopLevelKeywords } from '../structure/scanner';

import type { EditCtx } from './applyStructuralEdit';
import { deleteEdit, insertEdit, replaceEdit } from './applyStructuralEdit.shared';
import { clauseTreeRoot, resolveNodePath } from './whereNodePath';
import { endsInLineComment, outsideLineComments, parenthesize, parenthesizeCompound } from './wholePredicate';

type GroupOp = Extract<
  StructuralEdit,
  {
    op:
      | 'set-connector'
      | 'toggle-negate'
      | 'unwrap-group'
      | 'add-where-group'
      | 'wrap-group'
      | 'group-leaf'
      | 'move-node';
  }
>;

export function planWhereGroupEdit(ctx: EditCtx, edit: GroupOp): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'set-connector':
      return planSetConnector(ctx, edit);
    case 'toggle-negate':
      return planToggleNegate(ctx, edit);
    case 'unwrap-group':
      return planUnwrapGroup(ctx, edit);
    case 'add-where-group':
      return planAddWhereGroup(ctx, edit);
    case 'wrap-group':
      return planWrapGroup(ctx, edit);
    case 'group-leaf':
      return planGroupLeaf(ctx, edit);
    case 'move-node':
      return planMoveNode(ctx, edit);
  }
}

// Flip every connector joining a group's direct children to `edit.connector`.
// One replaceEdit per inter-child gap; skips gaps already at the target.
function planSetConnector(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'set-connector' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const group = resolveNodePath(root, edit.groupPath);
  if (!group || group.kind !== 'group' || group.children.length < 2) return [];
  const edits: PlannedByteEdit[] = [];
  for (let i = 0; i < group.children.length - 1; i++) {
    const gapStart = group.children[i].range.endOffset;
    const gapEnd = group.children[i + 1].range.startOffset;
    const kws = scanTopLevelKeywords(ctx.source, gapStart, gapEnd, ctx.options);
    const conn = kws.find((k) => k.upper === 'AND' || k.upper === 'OR');
    if (conn && conn.upper !== edit.connector) {
      edits.push(replaceEdit(ctx, conn.start, conn.end, edit.connector, 'set-connector'));
    }
  }
  return edits;
}

// Add / remove `NOT` on a leaf or group.
function planToggleNegate(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'toggle-negate' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const node = resolveNodePath(root, edit.path);
  if (!node) return [];
  const { startOffset, endOffset } = node.range;
  if (node.kind === 'group' && node.negated) {
    // Un-negate: drop the leading `NOT ` before the group's open paren.
    const paren = ctx.source.indexOf('(', startOffset);
    if (paren < startOffset || paren >= endOffset) return [];
    return [deleteEdit(ctx, startOffset, paren, 'toggle-negate:off')];
  }
  if (node.kind === 'group' && node.explicit) {
    // Already parenthesised — just prefix `NOT `.
    return [insertEdit(ctx, startOffset, 'NOT ', 'toggle-negate:on-group')];
  }
  // Leaf or implicit group — wrap in `NOT (…)`.
  return [
    insertEdit(ctx, startOffset, 'NOT (', 'toggle-negate:on-wrap'),
    insertEdit(ctx, endOffset, outsideLineComments(ctx, endOffset, endOffset, ')'), 'toggle-negate:on-wrap'),
  ];
}

// Drop an explicit, non-negated group's parens (lift its children into the parent).
function planUnwrapGroup(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'unwrap-group' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const group = resolveNodePath(root, edit.groupPath);
  if (!group || group.kind !== 'group' || !group.explicit || group.negated) return [];
  const { startOffset, endOffset } = group.range;
  if (ctx.source[startOffset] !== '(' || ctx.source[endOffset - 1] !== ')') return [];
  return [
    deleteEdit(ctx, startOffset, startOffset + 1, 'unwrap-group:open'),
    deleteEdit(ctx, endOffset - 1, endOffset, 'unwrap-group:close'),
  ];
}

// Append ` <combinator> (<text>)` inside / after the parent group.
function planAddWhereGroup(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-where-group' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const group = resolveNodePath(root, edit.parentPath);
  if (!group || group.kind !== 'group' || group.children.length === 0) return [];
  const at = group.children[group.children.length - 1].range.endOffset;
  const text = ` ${edit.combinator} ${parenthesize(edit.text, ctx.options)}`;
  return [insertEdit(ctx, at, outsideLineComments(ctx, at, at, text), 'add-where-group')];
}

// Wrap a contiguous run of a group's children [fromIndex..toIndex] in new parens.
// Two zero-width inserts at the run's outer byte boundaries; the run's existing
// inter-child connectors stay inside the new group.
function planWrapGroup(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'wrap-group' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const group = resolveNodePath(root, edit.parentPath);
  if (!group || group.kind !== 'group') return [];
  const { fromIndex, toIndex } = edit;
  if (fromIndex < 0 || toIndex >= group.children.length || fromIndex > toIndex) return [];
  const start = group.children[fromIndex].range.startOffset;
  const end = group.children[toIndex].range.endOffset;
  return [
    insertEdit(ctx, start, '(', 'wrap-group:open'),
    insertEdit(ctx, end, outsideLineComments(ctx, end, end, ')'), 'wrap-group:close'),
  ];
}

// Convert a node into a group by wrapping it and joining a new predicate:
// `<node>` → `(<node> <combinator> <text>)` — a multi-part `<text>` is itself
// parenthesised so it stays one member. Two zero-width inserts at the
// node's outer byte boundaries. Addresses the node DIRECTLY (not via a parent),
// so it works on a root-level leaf (path `[]`) with no enclosing group — the
// case `wrap-group` cannot serve. No-op when the path doesn't resolve or the
// predicate text is blank.
function planGroupLeaf(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'group-leaf' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root) return [];
  const node = resolveNodePath(root, edit.path);
  if (!node) return [];
  const text = edit.text.trim();
  if (text.length === 0) return [];
  const { startOffset, endOffset } = node.range;
  const member = parenthesizeCompound(text, ctx.options);
  const close = ` ${edit.combinator} ${member}${endsInLineComment(member, ctx.options) ? '\n' : ''})`;
  return [
    insertEdit(ctx, startOffset, '(', 'group-leaf:open'),
    insertEdit(ctx, endOffset, outsideLineComments(ctx, endOffset, endOffset, close), 'group-leaf:close'),
  ];
}

function samePath(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// Reorder a group's children: ONE atomic replace over the children span,
// re-emitting the source slices in arrayMove(fromIndex, toIndex) order joined
// by the group's connector. This is a whole-span re-emit (not the byte-splice
// planListMove does for comma lists) — rewriting the whole children span at
// once avoids connector juggling and overlapping edits. In-group only this
// slice: a cross-parent move (fromParentPath !== toParentPath) is a no-op
// (reserved).
function planMoveNode(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'move-node' }>,
): readonly PlannedByteEdit[] {
  const root = clauseTreeRoot(ctx.structure, edit.clause);
  if (!root || edit.fromPath.length === 0) return [];
  const fromParentPath = edit.fromPath.slice(0, -1);
  const fromIndex = edit.fromPath[edit.fromPath.length - 1];
  if (!samePath(fromParentPath, edit.toParentPath)) return []; // cross-group reserved
  const group = resolveNodePath(root, fromParentPath);
  if (!group || group.kind !== 'group') return [];
  const children = group.children;
  const n = children.length;
  const { toIndex } = edit;
  if (n < 2 || fromIndex < 0 || fromIndex >= n || toIndex < 0 || toIndex >= n || fromIndex === toIndex) {
    return [];
  }
  const spanStart = children[0].range.startOffset;
  const spanEnd = children[n - 1].range.endOffset;
  const texts = children.map((c) => ctx.source.slice(c.range.startOffset, c.range.endOffset));
  const [moved] = texts.splice(fromIndex, 1);
  texts.splice(toIndex, 0, moved);
  // A child ending in a line comment puts the next connector on a new line.
  const joined = texts.reduce((acc, t) =>
    `${acc}${endsInLineComment(acc, ctx.options) ? '\n' : ' '}${group.connector} ${t}`);
  return [replaceEdit(ctx, spanStart, spanEnd, outsideLineComments(ctx, spanStart, spanEnd, joined), 'move-node:reorder')];
}
